import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IconButton,
    Badge,
    Menu,
    MenuItem,
    Typography,
    Box,
    Button,
    Chip,
    Tooltip,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    Notifications,
    NotificationsNone,
    CheckCircle,
    Error,
    Warning,
    Info,
    MarkEmailRead
} from '@mui/icons-material';
import { useNotification } from '../context/NotificationContext';
import { useAlerts } from '../context/AlertContext';
import { useAuth } from '../context/useAuth';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import { formatRelativeTime } from '../utils/dateTime';

const AlertNotificationCenter = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { formatDate } = useTimezoneFormatter();
    const isPlatformAdmin = ['r1', 'r2'].includes(user?.roleId);

    const { addNotification } = useNotification();
    const {
        alerts,
        unreadCount,
        loading,
        fetchAlerts,
        markAsRead,
        markAllRead,
        clearAllAlerts,
        deleteAlert,
        showTenantAlerts,
        toggleTenantAlerts
    } = useAlerts();

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        fetchAlerts(); // Trigger refresh on open
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMarkAsRead = async (alertId) => {
        await markAsRead(alertId);
    };

    const handleMarkAllRead = async () => {
        await markAllRead();
        addNotification('All alerts marked as read', 'success');
    };

    const handleDeleteAlert = async (alertId) => {
        await deleteAlert(alertId);
        addNotification('Alert deleted', 'success');
    };

    const getAlertIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />;
            case 'error': return <Error sx={{ color: '#ef4444', fontSize: 20 }} />;
            case 'warning': return <Warning sx={{ color: '#f59e0b', fontSize: 20 }} />;
            case 'info':
            default: return <Info sx={{ color: '#3b82f6', fontSize: 20 }} />;
        }
    };

    const getAlertColor = (type) => {
        switch (type) {
            case 'success': return '#10b981';
            case 'error': return '#ef4444';
            case 'warning': return '#f59e0b';
            case 'info':
            default: return '#3b82f6';
        }
    };


    return (
        <>
            <Tooltip title="Notifications">
                <IconButton
                    onClick={handleClick}
                    size="large"
                    className="text-gray-500 dark:text-gray-400"
                >
                    <Badge badgeContent={unreadCount} color="error">
                        {unreadCount > 0 ? <Notifications /> : <NotificationsNone />}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: 400,
                        maxHeight: 600,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Header */}
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Notifications
                    </Typography>

                    {isPlatformAdmin && (
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={showTenantAlerts}
                                    onChange={toggleTenantAlerts}
                                />
                            }
                            label={<Typography variant="caption">Tenant Alerts</Typography>}
                            sx={{ ml: 2, mr: 'auto' }}
                        />
                    )}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {unreadCount > 0 && (
                            <Button size="small" onClick={handleMarkAllRead}>
                                Mark all read
                            </Button>
                        )}
                        {alerts.length > 0 && (
                            <Button size="small" color="error" onClick={clearAllAlerts}>
                                Clear
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Alert List */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 500 }}>
                    {loading && (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography color="text.secondary">Loading...</Typography>
                        </Box>
                    )}

                    {!loading && alerts.length === 0 && (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <NotificationsNone sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography color="text.secondary">No notifications</Typography>
                        </Box>
                    )}

                    {!loading && alerts.map((alert) => (
                        <MenuItem
                            key={alert.id}
                            sx={{
                                borderLeft: `4px solid ${getAlertColor(alert.alert_type)}`,
                                bgcolor: alert.is_read ? 'transparent' : 'action.hover',
                                py: 1.5,
                                px: 2,
                                display: 'block',
                                whiteSpace: 'normal',
                                '&:hover': {
                                    bgcolor: 'action.selected'
                                }
                            }}
                            onClick={() => !alert.is_read && handleMarkAsRead(alert.id)}
                        >
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'start' }}>
                                {getAlertIcon(alert.alert_type)}
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: alert.is_read ? 400 : 600 }}>
                                            {alert.title}
                                        </Typography>
                                        {!alert.is_read && (
                                            <Chip label="New" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                                        {alert.message}
                                    </Typography>

                                    {alert.cta && (
                                        <Button 
                                            variant="contained" 
                                            size="small" 
                                            sx={{ 
                                                mb: 1.5, 
                                                textTransform: 'none',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                boxShadow: 'none',
                                                '&:hover': { boxShadow: 'none' }
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!alert.is_read) handleMarkAsRead(alert.id);
                                                handleClose();
                                                navigate(alert.cta.link, { state: { forceTab: 'password' } });
                                            }}
                                        >
                                            {alert.cta.label}
                                        </Button>
                                    )}

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Tooltip title={formatDate(alert.created_at)}>
                                            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                                                {formatRelativeTime(alert.created_at)}
                                            </Typography>
                                        </Tooltip>
                                        <Button
                                            size="small"
                                            color="error"
                                            sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.7rem' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteAlert(alert.id);
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </MenuItem>
                    ))}
                </Box>

                {/* Footer */}
                {alerts.length > 0 && (
                    <Box sx={{ p: 1, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
                        <Button size="small" fullWidth onClick={handleClose}>
                            Close
                        </Button>
                    </Box>
                )}
            </Menu>
        </>
    );
};

export default AlertNotificationCenter;
